import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

import { usePageTitle } from "@/contexts/PageTitleContext";
import { useTenant } from "@/contexts/TenantContext";
import { useToast } from "@/hooks/use-toast";
import { useHasModule } from "@/hooks/usePermissions";
import { getStoredUser } from "@/lib/auth";
import { api } from "@/lib/api";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

import InCardFilterPanel from "@/components/leads/InCardFilterPanel";
import LeadTableColumnsDialog from "@/components/leads/LeadTableColumnsDialog";
import CustomViewsDialog from "@/components/leads/CustomViewsDialog";
import { CollapsibleSearch } from "@/components/ui/collapsible-search";

import BuildModeSelector from "@/components/funnels/BuildModeSelector";
import TemplatePickerModal from "@/components/funnels/TemplatePickerModal";
import AIFunnelBriefModal from "@/components/funnels/AIFunnelBriefModal";
import DeleteConfirmDialog from "@/components/funnels/DeleteConfirmDialog";
import { FunnelHealthBadge } from "@/components/funnels/FunnelHealthBadge";

import { 
  ArrowUpDown, 
  ChevronDown, 
  Download, 
  ListFilter, 
  Plus, 
  Settings2, 
  Trash2,
  Sparkles,
  MessageSquare,
  Check,
  Filter
} from "lucide-react";

// ====== HELPER FUNCTIONS ======

function prettifyFunnelFieldLabel(key) {
  const map = {
    name: "Name",
    slug: "Slug",
    funnel_type: "Type",
    status: "Status",
    health: "Health",
    updated_at: "Updated",
    created_at: "Created",
    published_at: "Published",
  };
  return map[key] || key;
}

function toCsvCell(v) {
  const s = String(v ?? "");
  if (s.includes(",") || s.includes("\n") || s.includes('"')) {
    return '"' + s.replaceAll('"', '""') + '"';
  }
  return s;
}

function downloadCsv(filename, rows) {
  const csv = rows.map((r) => r.map(toCsvCell).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function useDebouncedValue(value, delayMs = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function getFunnelRowField(row, key) {
  if (!row) return "";
  
  if (key === "updated_at" || key === "created_at" || key === "published_at") {
    const raw = row[key];
    if (!raw) return "";
    return format(new Date(raw), "MMM d, yyyy");
  }
  
  if (key === "status") {
    if (!row.is_active) return "Inactive";
    return row.published_at ? "Published" : "Draft";
  }
  
  return row[key] ?? "";
}

// ====== CHIP COMPONENT ======

const CHIP_CLASSES = [
  "bg-indigo-500/20 text-indigo-100 ring-indigo-400/20",
  "bg-emerald-500/20 text-emerald-100 ring-emerald-400/20",
  "bg-cyan-500/20 text-cyan-100 ring-cyan-400/20",
  "bg-fuchsia-500/20 text-fuchsia-100 ring-fuchsia-400/20",
  "bg-amber-500/20 text-amber-100 ring-amber-400/20",
];

function hashString(s) {
  let h = 0;
  const str = String(s || "");
  for (let i = 0; i < str.length; i += 1) {
    h = (h * 31 + str.charCodeAt(i)) >>> 0;
  }
  return h;
}

function Chip({ value }) {
  const v = String(value || "—");
  const cls = CHIP_CLASSES[hashString(v) % CHIP_CLASSES.length];
  return (
    <Badge className={`ring-1 max-w-full overflow-hidden whitespace-nowrap ${cls}`}>
      <span className="min-w-0 truncate">{v}</span>
    </Badge>
  );
}

// ====== STATUS BADGE ======

function StatusBadge({ hasPublished, isActive, isAIGenerated }) {
  if (!isActive) {
    return <Badge variant="outline" className="text-slate-200/60">Inactive</Badge>;
  }
  
  return (
    <div className="flex items-center gap-1.5">
      {hasPublished ? (
        <Badge className="bg-green-500/10 text-green-400 hover:bg-green-500/20 ring-1 ring-green-400/20">Published</Badge>
      ) : (
        <Badge variant="outline" className="text-slate-200/60">Draft</Badge>
      )}
      {isAIGenerated && (
        <Badge variant="outline" className="gap-1 text-purple-400 border-purple-500/30">
          <Sparkles className="h-3 w-3" />
        </Badge>
      )}
    </div>
  );
}

// ====== RENDER CELL ======

function renderFunnelCell(row, key) {
  if (key === "funnel_type") return <Chip value={row.funnel_type} />;
  if (key === "status") {
    return (
      <StatusBadge 
        hasPublished={!!row.published_at} 
        isActive={row.is_active !== false}
        isAIGenerated={row.metadata?.created_by === "funnel_assist"}
      />
    );
  }
  if (key === "health") {
    return <FunnelHealthBadge score={row.health_score} status={row.health_status} />;
  }
  return String(getFunnelRowField(row, key) ?? "");
}

// ====== CONSTANTS ======

const DEFAULT_TABLE_COLUMNS = ["name", "slug", "funnel_type", "status", "health", "updated_at"];
const DEFAULT_VISIBLE_FILTERS = ["name", "funnel_type", "status"];
const VIEW_LABEL = "All Funnels";
const DEFAULT_LIMIT = 10;

// ====== MAIN COMPONENT ======

export default function FunnelsPage() {
  usePageTitle("Funnels");
  const { toast } = useToast();
  const navigate = useNavigate();
  const user = getStoredUser();
  const { selectedTenantId } = useTenant();

  // MA-PHASE10: Use module-based permissions instead of role checks
  // MA-RBAC-POST-BATCH3-FRONTEND-CONSISTENCY-AUDIT-124: canonical 'funnels' module (was deprecated alias 'funnel_analytics')
  const { hasAccess: canManageFunnels } = useHasModule("funnels");

  const [selected, setSelected] = useState(new Set());

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  // MA-UI-113: Filters visible by default, persist user preference
  const [filtersOpen, setFiltersOpen] = useState(() => {
    try {
      const stored = localStorage.getItem("funnels_filters_open");
      // If user has explicitly set a preference, use it; otherwise default to true (visible)
      return stored !== null ? stored === "true" : true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("funnels_filters_open", String(filtersOpen));
    } catch {
      // ignore
    }
  }, [filtersOpen]);

  const [filtersDraft, setFiltersDraft] = useState({});
  const [filtersApplied, setFiltersApplied] = useState({});

  // Search
  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 250);

  // Columns
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [tableColumns, setTableColumns] = useState(DEFAULT_TABLE_COLUMNS);

  // Custom Views
  const [customViews, setCustomViews] = useState([]);
  const [activeCustomViewId, setActiveCustomViewId] = useState("all");
  const [viewsDialogOpen, setViewsDialogOpen] = useState(false);
  const [prefsLoading, setPrefsLoading] = useState(true);

  // Pagination
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);

  // Sorting
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  // Modals
  const [showBuildMode, setShowBuildMode] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showAIBrief, setShowAIBrief] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, funnel: null });

  // ====== PREFERENCES PERSISTENCE ======

  // Load preferences on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/user-prefs");
        const prefs = res.data?.funnels || {};
        if (Array.isArray(prefs.table_columns) && prefs.table_columns.length > 0) {
          setTableColumns(prefs.table_columns);
        }
        if (Array.isArray(prefs.custom_views)) {
          setCustomViews(prefs.custom_views);
        }
        if (typeof prefs.active_custom_view_id === "string") {
          setActiveCustomViewId(prefs.active_custom_view_id);
          // If a custom view is active, load its columns
          if (prefs.active_custom_view_id !== "all" && Array.isArray(prefs.custom_views)) {
            const view = prefs.custom_views.find(v => v.id === prefs.active_custom_view_id);
            if (view && Array.isArray(view.columns) && view.columns.length > 0) {
              setTableColumns(view.columns);
            }
          }
        }
      } catch (e) {
        // ignore
      } finally {
        setPrefsLoading(false);
      }
    })();
  }, []);

  // Save preferences helper
  const saveFunnelsPrefs = useCallback(async (updates) => {
    try {
      const res = await api.get("/user-prefs");
      const currentPrefs = res.data || {};
      const funnelsPrefs = {
        ...(currentPrefs.funnels || {}),
        ...updates,
      };
      await api.put("/user-prefs", { ...currentPrefs, funnels: funnelsPrefs });
      toast({ title: "Preferences saved" });
    } catch (e) {
      toast({ title: "Could not save preferences", variant: "destructive" });
    }
  }, [toast]);

  // ====== DATA FETCHING ======

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api.get("/funnels");
      setItems(res.data || []);
      setSelected(new Set());
    } catch (e) {
      // MA-PHASE11A-P0-AUDIT-005: On error, clear items to prevent stale data from previous account
      console.error("Failed to load funnels:", e);
      setItems([]);  // Clear stale data
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // MA-PHASE11A-P0-AUDIT-005: Clear items immediately when account switches, before refetch
    // MA-PHASE11A-POSTLOGIN-ROUTING-FIX-011: Only fetch when selectedTenantId exists
    if (!selectedTenantId) return;
    
    setItems([]);
    
    // MA-PHASE10: Permission check handled by RequireModule guard in routing
    // No need for redundant user role checks here
    if (!user) {
      navigate("/app", { replace: true });
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, selectedTenantId]);

  useEffect(() => {
    setOffset(0);
  }, [filtersApplied, qDebounced]);

  // ====== FILTER FIELDS ======

  const filterFields = useMemo(() => {
    return [
      { key: "name", label: "Name", type: "text" },
      { key: "status", label: "Status", type: "text" },
      { key: "funnel_type", label: "Type", type: "text" },
    ];
  }, []);

  const optionsByField = useMemo(() => ({}), []);

  // ====== FILTERING ======

  const filtered = useMemo(() => {
    const needle = qDebounced.trim().toLowerCase();
    const applied = filtersApplied || {};

    return (items || []).filter((row) => {
      if (needle) {
        const hay = [row.name, row.slug, row.funnel_type]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }

      const clauses = Object.entries(applied)
        .filter(([, v]) => v?.enabled)
        .map(([k, v]) => [k, v]);

      for (const [k, cfg] of clauses) {
        const op = cfg.operator;
        const val = String(cfg.value || "").trim().toLowerCase();
        if (!val) continue;

        let fieldValue = "";
        if (k === "name") fieldValue = String(row.name || "");
        else if (k === "funnel_type") fieldValue = String(row.funnel_type || "");
        else if (k === "status") {
          if (!row.is_active) fieldValue = "inactive";
          else fieldValue = row.published_at ? "published" : "draft";
        }

        const fv = fieldValue.toLowerCase();
        if (op === "is" && fv !== val) return false;
        if (op === "contains" && !fv.includes(val)) return false;
        if (op === "starts_with" && !fv.startsWith(val)) return false;
        if (op === "ends_with" && !fv.endsWith(val)) return false;
        if (op === "isnt" && fv === val) return false;
      }

      return true;
    });
  }, [items, qDebounced, filtersApplied]);

  const filtersActive = useMemo(() => {
    return Object.values(filtersApplied || {}).some((v) => v?.enabled);
  }, [filtersApplied]);

  const clearFilters = () => {
    setFiltersDraft({});
    setFiltersApplied({});
  };

  // ====== SORTING ======

  const applySort = (field, dir) => {
    setSortBy(field);
    setSortDir(dir);
  };

  const sortedRows = useMemo(() => {
    const rows = Array.isArray(filtered) ? [...filtered] : [];
    if (!sortBy) return rows;
    const mult = sortDir === "desc" ? -1 : 1;
    rows.sort((a, b) => {
      const av = String(getFunnelRowField(a, sortBy) ?? "");
      const bv = String(getFunnelRowField(b, sortBy) ?? "");
      return av.localeCompare(bv, undefined, { sensitivity: "base" }) * mult;
    });
    return rows;
  }, [filtered, sortBy, sortDir]);

  // ====== PAGINATION ======

  const COL_MIN_PX = 160;
  const tableMinWidthPx = useMemo(() => {
    const colCount = (tableColumns || []).length;
    return 46 + 44 + 80 + Math.max(colCount, 1) * COL_MIN_PX;
  }, [tableColumns]);

  const pageRows = useMemo(() => {
    return sortedRows.slice(offset, offset + limit);
  }, [sortedRows, offset, limit]);

  const total = sortedRows.length;

  // ====== SELECTION ======

  const allOnPageSelected = useMemo(
    () => pageRows.length > 0 && pageRows.every((x) => selected.has(x.id)),
    [pageRows, selected]
  );

  const toggleAllOnPage = (checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        pageRows.forEach((x) => next.add(x.id));
      } else {
        pageRows.forEach((x) => next.delete(x.id));
      }
      return next;
    });
  };

  const anySelected = selected.size > 0;

  const selectedRows = useMemo(() => sortedRows.filter((x) => selected.has(x.id)), [sortedRows, selected]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ====== ACTIONS ======

  const exportSelectedCsv = async () => {
    const fields = tableColumns || [];
    if (fields.length === 0 || !anySelected) return;

    const rows = [fields.map((k) => prettifyFunnelFieldLabel(k))];
    selectedRows.forEach((r) => {
      rows.push(fields.map((k) => getFunnelRowField(r, k) ?? ""));
    });

    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`funnels_selected_${stamp}.csv`, rows);
    toast({ title: "Export downloaded" });
  };

  const deleteSelected = async () => {
    try {
      await Promise.all(selectedRows.map((r) => api.delete(`/funnels/${r.id}`)));
      toast({ title: "Deleted" });
      setSelected(new Set());
      refresh();
    } catch (e) {
      toast({ title: "Could not delete", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    }
  };

  const handleNewFunnel = () => {
    setShowBuildMode(true);
  };

  const handleBuildModeSelect = (mode) => {
    setShowBuildMode(false);
    if (mode === "manual") {
      setShowTemplatePicker(true);
    } else if (mode === "ai") {
      setShowAIBrief(true);
    }
  };

  const handleEdit = (funnel) => {
    navigate(`/app/funnels/${funnel.id}`);
  };

  const handleDelete = (funnel) => {
    setDeleteConfirm({ open: true, funnel });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.funnel) return;
    try {
      await api.delete(`/funnels/${deleteConfirm.funnel.id}`);
      toast({ title: "Funnel deleted" });
      refresh();
    } catch (e) {
      toast({
        title: "Delete failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleteConfirm({ open: false, funnel: null });
    }
  };

  // Get current view label
  const currentViewLabel = useMemo(() => {
    if (activeCustomViewId === "all") return VIEW_LABEL;
    const view = customViews.find(v => v.id === activeCustomViewId);
    return view?.name || VIEW_LABEL;
  }, [activeCustomViewId, customViews]);

  // ====== MAIN RENDER ======

  return (
    <div className="flex flex-col h-[calc(100vh-230px)] p-6 gap-4" data-testid="funnels-page">
      {/* Controls row */}
      <div className="flex flex-col gap-2 rounded-2xl bg-black/0 px-1 sm:flex-row sm:items-center sm:justify-between shrink-0">
        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                data-testid="funnels-view"
                aria-label="View"
              >
                {currentViewLabel}
                <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="glass border-white/10 text-slate-100 min-w-[180px]">
              {/* All Funnels option */}
              <DropdownMenuItem
                onClick={() => {
                  setActiveCustomViewId("all");
                  setTableColumns(DEFAULT_TABLE_COLUMNS);
                  setFiltersApplied({});
                  setFiltersDraft({});
                  saveFunnelsPrefs({ active_custom_view_id: "all" });
                }}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                data-testid="funnels-view-all"
              >
                <span className="flex-1">{VIEW_LABEL}</span>
                {activeCustomViewId === "all" && <Check className="h-4 w-4 ml-2 text-indigo-400" />}
              </DropdownMenuItem>
              
              {/* Custom views */}
              {customViews.length > 0 && (
                <>
                  <DropdownMenuSeparator className="bg-white/10" />
                  {customViews.map((view) => (
                    <DropdownMenuItem
                      key={view.id}
                      onClick={() => {
                        setActiveCustomViewId(view.id);
                        if (Array.isArray(view.columns) && view.columns.length > 0) {
                          setTableColumns(view.columns);
                        }
                        if (view.filters_applied) {
                          setFiltersApplied(view.filters_applied);
                          setFiltersDraft(view.filters_applied);
                        }
                        saveFunnelsPrefs({ active_custom_view_id: view.id });
                      }}
                      className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                    >
                      <span className="flex-1 truncate">{view.name}</span>
                      {activeCustomViewId === view.id && <Check className="h-4 w-4 ml-2 text-indigo-400" />}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              
              {/* Manage Views option */}
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                onClick={() => setViewsDialogOpen(true)}
                className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                data-testid="funnels-manage-views"
              >
                <Settings2 className="h-4 w-4 mr-2" />
                Manage Views...
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {anySelected ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="funnels-actions">
                  Actions
                  <ChevronDown className="ml-2 h-4 w-4 opacity-80" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="glass border-white/10 text-slate-100">
                {/* MA-PHASE10: Hide Delete action if user lacks funnel_analytics module */}
                <DropdownMenuItem
                  onClick={exportSelectedCsv}
                  className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                  data-testid="funnels-export-selected"
                >
                  <Download className="mr-2 h-4 w-4" /> Export (CSV)
                </DropdownMenuItem>
                {canManageFunnels && (
                  <DropdownMenuItem
                    onClick={deleteSelected}
                    className="text-rose-200 focus:bg-rose-500/15 focus:text-rose-100"
                    data-testid="funnels-delete-selected"
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
          <CollapsibleSearch
            value={q}
            onChange={setQ}
            placeholder="Search funnels..."
            width="w-[280px]"
            data-testid="funnels-search"
          />

          {/* Filter Button - matches Leads page styling */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setFiltersOpen(!filtersOpen)}
            aria-label="Open filters"
            data-testid="funnels-filter-icon"
            className="relative"
          >
            <ListFilter className={`h-4 w-4 ${filtersOpen ? "text-indigo-400" : ""}`} />
            {filtersActive ? <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-indigo-400 ring-2 ring-slate-950" /> : null}
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setColumnsOpen(true)}
            aria-label="Customize columns"
            data-testid="funnels-customize-columns"
          >
            <Settings2 className="h-4 w-4" />
          </Button>

          {/* MA-PHASE10: Hide New Funnel button if user lacks funnel_analytics module */}
          {canManageFunnels && (
            <Button onClick={handleNewFunnel} data-testid="create-funnel-btn">
              <Plus className="mr-2 h-4 w-4" /> New Funnel
            </Button>
          )}
        </div>
      </div>

      {/* Filter + List cards */}
      <div
        className={
          "grid min-w-0 grid-cols-1 gap-3 flex-1 min-h-0 " +
          (filtersOpen ? "lg:grid-cols-[360px_1fr]" : "lg:grid-cols-1")
        }
      >
        {filtersOpen ? (
          <Card className="glass flex flex-col min-h-0 h-full" data-testid="funnels-filter-card">
            <CardContent className="h-full p-0">
              <InCardFilterPanel
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
                title="Filter Funnels by"
                fields={filterFields}
                optionsByField={optionsByField}
                draft={filtersDraft}
                onChangeDraft={setFiltersDraft}
                onApply={() => setFiltersApplied(filtersDraft)}
                onClear={clearFilters}
              />
            </CardContent>
          </Card>
        ) : null}

        <Card className="glass flex flex-col min-h-0 h-full min-w-0 w-full" data-testid="funnels-list-card">
          <CardContent className="flex h-full flex-col p-0">
            {loading ? (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-200/70">
                Loading…
              </div>
            ) : (
            <div data-testid="funnels-table-wrap" className="flex-1 min-w-0 overflow-hidden">
              <div
                className="h-full w-full min-w-0 overflow-auto"
                data-testid="funnels-hscroll"
                onWheel={(e) => {
                  if (e.shiftKey) {
                    e.preventDefault();
                    e.currentTarget.scrollLeft += e.deltaY;
                  }
                }}
              >
                <Table style={{ minWidth: tableMinWidthPx }} containerClassName="h-full">
                  <TableHeader className="relative">
                    <TableRow className="border-transparent hover:bg-transparent">
                      <TableHead colSpan={(tableColumns || []).length + 1} className="relative h-auto p-1 text-slate-200/70">
                        <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-px -translate-y-[11px] bg-white/10" />
                        <div className="w-full px-2 py-3 grid grid-cols-[46px_1fr_44px] items-center gap-3">
                          <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={allOnPageSelected}
                              onCheckedChange={toggleAllOnPage}
                              className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                              data-testid="funnels-select-all"
                            />
                          </div>

                          <div
                            className="min-w-0 grid items-center gap-3 pr-2"
                            style={{ gridTemplateColumns: `repeat(${(tableColumns || []).length}, minmax(160px, 1fr))` }}
                            data-testid="funnels-header-grid"
                          >
                            {(tableColumns || []).map((k) => (
                              <div key={k} className="flex min-w-0 items-center justify-between gap-2 pr-2 text-xs">
                                <span className="min-w-0 flex-1 truncate">{prettifyFunnelFieldLabel(k)}</span>
                                <button
                                  type="button"
                                  className={
                                    "inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/10 focus:outline-none" +
                                    (sortBy === k ? " bg-white/10" : "")
                                  }
                                  aria-label={`Sort ${prettifyFunnelFieldLabel(k)}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const nextDir = sortBy === k && sortDir === "asc" ? "desc" : "asc";
                                    applySort(k, nextDir);
                                  }}
                                  data-testid={`funnels-sort-${k}`}
                                >
                                  <ArrowUpDown className="h-3.5 w-3.5 opacity-80" />
                                </button>
                              </div>
                            ))}
                          </div>

                          <div className="h-9 w-9" />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {sortedRows.length === 0 ? (
                      <TableRow className="border-transparent">
                        <TableCell colSpan={(tableColumns || []).length + 1} className="py-16">
                          <div className="sticky left-1/2 -translate-x-1/2 w-max flex flex-col items-center justify-center">
                            <Filter className="h-12 w-12 text-indigo-400 mb-3" />
                            <div className="text-sm text-slate-200/70">No funnels found.</div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : pageRows.map((row) => (
                        <TableRow key={row.id} className="border-transparent" data-testid={`funnel-row-${row.id}`}>
                          <TableCell colSpan={(tableColumns || []).length + 1} className="p-1 align-top">
                            <div
                              role="button"
                              tabIndex={0}
                              className="w-full rounded-xl bg-white/5 px-2 py-3 text-left hover:bg-white/10 hover:ring-1 hover:ring-white/10"
                              onClick={() => handleEdit(row)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleEdit(row);
                                }
                              }}
                              data-testid={`funnel-row-button-${row.id}`}
                            >
                              <div className="grid grid-cols-[46px_1fr_44px] items-center gap-3">
                                <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    checked={selected.has(row.id)}
                                    onCheckedChange={() => toggleOne(row.id)}
                                    className="border-white/20 data-[state=checked]:bg-indigo-500/90 data-[state=checked]:border-indigo-400/40"
                                    data-testid={`funnel-row-select-${row.id}`}
                                  />
                                </div>

                                <div className="min-w-0 grid grid-cols-1">
                                  <div
                                    className="grid items-center gap-3 pr-2"
                                    style={{ gridTemplateColumns: `repeat(${(tableColumns || []).length}, minmax(160px, 1fr))` }}
                                    data-testid="funnel-row-grid"
                                  >
                                    {(tableColumns || []).map((k) => (
                                      <div key={k} className="min-w-0">
                                        {k === "funnel_type" || k === "status" ? (
                                          renderFunnelCell(row, k)
                                        ) : k === "name" ? (
                                          <div>
                                            <div className="truncate text-slate-100/90 font-medium">{row.name}</div>
                                            <div className="text-xs text-slate-200/60">/{row.slug}</div>
                                          </div>
                                        ) : (
                                          <div className="truncate text-slate-100/90">{renderFunnelCell(row, k)}</div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8">
                                        <ChevronDown className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className="glass border-white/10 text-slate-100">
                                      {/* MA-PHASE10: Hide Edit/Delete if user lacks funnel_analytics module */}
                                      {canManageFunnels && (
                                        <>
                                          <DropdownMenuItem
                                            onClick={() => handleEdit(row)}
                                            className="text-slate-100 focus:bg-white/10 focus:text-slate-100"
                                          >
                                            Edit
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() => handleDelete(row)}
                                            className="text-rose-200 focus:bg-rose-500/15 focus:text-rose-100"
                                          >
                                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                                          </DropdownMenuItem>
                                        </>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    }
                  </TableBody>
                </Table>
              </div>
            </div>
            )}

            <div data-testid="funnels-footer" className="h-[60px] border-t border-white/10 px-4 flex items-center justify-between shrink-0">
              <div className="text-xs text-slate-200/70" data-testid="funnels-count">
                {anySelected ? `Selected: ${selected.size}` : `Matching current filters: ${total}`}
              </div>

              <div className="flex flex-wrap items-center gap-2" data-testid="funnels-pagination">
                {offset > 0 ? (
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={offset === 0}
                    onClick={() => setOffset((o) => Math.max(0, o - limit))}
                    data-testid="funnels-prev"
                  >
                    Previous
                  </Button>
                ) : null}

                {offset + limit < total ? (
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={offset + limit >= total}
                    onClick={() => setOffset((o) => o + limit)}
                    data-testid="funnels-next"
                  >
                    Next
                  </Button>
                ) : null}

                <Select
                  value={String(limit)}
                  onValueChange={(v) => {
                    const next = Number(v);
                    setLimit(next);
                    setOffset(0);
                  }}
                >
                  <SelectTrigger className="h-9 w-[110px] rounded-full bg-white/15 text-slate-100 ring-1 ring-white/20" data-testid="funnels-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="glass border-white/10">
                    {[10, 25, 50, 100].map((n) => (
                      <SelectItem key={n} value={String(n)} data-testid={`funnels-page-size-${n}`}>
                        {n} / Page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <BuildModeSelector
        open={showBuildMode}
        onClose={() => setShowBuildMode(false)}
        onSelectMode={handleBuildModeSelect}
      />
      <TemplatePickerModal
        open={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onCreated={(funnelId) => {
          setShowTemplatePicker(false);
          navigate(`/app/funnels/${funnelId}`);
        }}
      />
      <AIFunnelBriefModal
        open={showAIBrief}
        onClose={() => setShowAIBrief(false)}
        onCreated={(funnelId) => {
          setShowAIBrief(false);
          // MA-AI-REVIEW-STEP-DECOUPLE-144: Direct to Builder (AI Review decoupled)
          // AI Review layer is intentionally disabled in Standalone Funnel Builder.
          // Feature is preserved for future use in PPB or advanced onboarding flows.
          // Previous flow: nav(`/app/funnels/${funnelId}/review`)
          // New flow: Direct to Builder
          navigate(`/app/funnels/${funnelId}`);
        }}
      />
      <LeadTableColumnsDialog
        open={columnsOpen}
        onOpenChange={setColumnsOpen}
        fields={[
          { key: "name", label: "Name" },
          { key: "slug", label: "Slug" },
          { key: "funnel_type", label: "Type" },
          { key: "status", label: "Status" },
          { key: "updated_at", label: "Updated" },
          { key: "created_at", label: "Created" },
        ]}
        value={tableColumns}
        onSave={(nextColumns) => {
          setTableColumns(nextColumns);
          // Persist column changes
          saveFunnelsPrefs({ table_columns: nextColumns });
        }}
      />
      <CustomViewsDialog
        open={viewsDialogOpen}
        onOpenChange={setViewsDialogOpen}
        value={customViews}
        currentDraft={{ filters_applied: filtersApplied }}
        currentColumns={tableColumns}
        onSave={(nextViews) => {
          setCustomViews(nextViews);
          saveFunnelsPrefs({ custom_views: nextViews });
        }}
      />
      <DeleteConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm((prev) => ({ ...prev, open }))}
        title="Delete Funnel"
        description={`Are you sure you want to delete "${deleteConfirm.funnel?.name}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
